"use client"
import { useState, useEffect, useContext } from "react"
import {
  Box,
  TextField,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Button,
  Typography,
  InputAdornment,
  Chip,
  Tooltip,
  Card,
  CardContent,
  Divider,
  useTheme,
  alpha,
  CircularProgress,
  Alert,
  Fade,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material"
import EditIcon from "@mui/icons-material/Edit"
import DeleteIcon from "@mui/icons-material/Delete"
import SearchIcon from "@mui/icons-material/Search"
import PersonIcon from "@mui/icons-material/Person"
import EmailIcon from "@mui/icons-material/Email"
import PhoneIcon from "@mui/icons-material/Phone"
import BadgeIcon from "@mui/icons-material/Badge"
import RefreshIcon from "@mui/icons-material/Refresh"
import { Visibility, VisibilityOff } from "@mui/icons-material"
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount"
import { ErpContext } from "../../context/ErpContext.js"
import ModalEditarUsuario from "./ModalEditarUsuario.js"
import CustomPagination from "../transaccionComponent/CustomPagination.js"

export default function TablaUsuariosComponent() {
  const theme = useTheme()
  const { getUsuario, deleteUsuarioSoft, user, isNavbarCollapsed } = useContext(ErpContext)

  const [usuarios, setUsuarios] = useState([])
  const [searchText, setSearchText] = useState("")
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)

  const [showPasswordForId, setShowPasswordForId] = useState(null)
  const [openModalEditarUsuario, setOpenModalEditarUsuario] = useState(false)
  const [usuarioEditar, setUsuarioEditar] = useState({})
  const [confirmDelete, setConfirmDelete] = useState({ open: false, usuario: null })

  const [page, setPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)

  useEffect(() => {
    fetchUsuarios()
  }, [page, rowsPerPage, searchText])

  const fetchUsuarios = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await getUsuario(page, rowsPerPage, searchText)
      setUsuarios(data.data || [])
      setTotalPages(data.totalPages || 1)
      setTotalCount(data.total || 0)
    } catch (err) {
      console.error("Error al cargar usuarios:", err)
      setError("No se pudieron cargar los usuarios. Intente nuevamente.")
    } finally {
      setLoading(false)
    }
  }

  const handleTogglePasswordVisibility = (id) => {
    setShowPasswordForId((prevId) => (prevId === id ? null : id))
  }

  const handleCerrarModalCrearUsuario = async (refresh = false) => {
    setOpenModalEditarUsuario(false)
    if (refresh) {
      await fetchUsuarios()
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
    }
  }

  const editarUsuario = (usuario) => {
    console.log("Usuario a editar:", usuario)
    setUsuarioEditar(usuario)
    setOpenModalEditarUsuario(true)
  }

  const handlePageChange = (newPage) => {
    setPage(newPage)
  }

  const handleSearchChange = (e) => {
    setSearchText(e.target.value)
    setPage(1) // Resetear a primera página cuando se busca
  }

  return (
    <Box sx={{ p: 0, overflow: "hidden", height: "100%", display: "flex", flexDirection: "column" }}>

      <Fade in={success}>
        <Alert
          severity="success"
          sx={{
            mb: 1,
            borderRadius: 1,
            boxShadow: 1,
          }}
          onClose={() => setSuccess(false)}
        >
          Operación completada con éxito
        </Alert>
      </Fade>

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 1,
            borderRadius: 1,
            boxShadow: 1,
          }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Mini menú de búsqueda arriba de la tabla */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 1,
          px: 0.5,
        }}
      >
        <TextField
          placeholder="Buscar por nombre de usuario..."
          value={searchText}
          onChange={handleSearchChange}
          variant="outlined"
          size="small"
          sx={{
            flex: 1,
            maxWidth: 300,
            "& .MuiOutlinedInput-root": {
              borderRadius: 1,
              fontSize: "0.875rem",
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={fetchUsuarios}
          disabled={loading}
          sx={{
            textTransform: "none",
            borderColor: "#5b5b5f",
            color: "#5b5b5f",
            fontSize: "0.75rem",
            py: 0.5,
            px: 1.5,
            "&:hover": {
              borderColor: "#5b5b5f",
              backgroundColor: "rgba(91, 91, 95, 0.04)",
            },
          }}
        >
          Actualizar
        </Button>
      </Box>

      <Card
        elevation={2}
        sx={{
          borderRadius: 2,
          overflow: "hidden",
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        <Box 
          sx={{ 
            position: "relative",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          {loading && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(255, 255, 255, 0.7)",
                zIndex: 10,
              }}
            >
              <CircularProgress />
            </Box>
          )}

          <TableContainer
            sx={{
              flex: 1,
              overflowY: "auto",
              minHeight: 0,
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#f1f1f1",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#888",
                borderRadius: "4px",
                "&:hover": {
                  backgroundColor: "#555",
                },
              },
            }}
          >
            <Table stickyHeader sx={{ tableLayout: "fixed" }}>
              <TableHead>
                <TableRow sx={{ height: "45px" }}>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#5b5b5f",
                      color: "white",
                      width: "5%",
                      padding: "0 16px",
                      height: "45px !important",
                      lineHeight: "45px",
                    }}
                  >
                    ID
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#5b5b5f",
                      color: "white",
                      width: "12%",
                      padding: "0 16px",
                      height: "45px !important",
                      lineHeight: "45px",
                    }}
                  >
                    Usuario
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#5b5b5f",
                      color: "white",
                      width: "15%",
                      padding: "0 16px",
                      height: "45px !important",
                      lineHeight: "45px",
                    }}
                  >
                    Contraseña
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#5b5b5f",
                      color: "white",
                      width: "15%",
                      padding: "0 16px",
                      height: "45px !important",
                      lineHeight: "45px",
                    }}
                  >
                    Nombre Completo
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#5b5b5f",
                      color: "white",
                      width: "12%",
                      padding: "0 16px",
                      height: "45px !important",
                      lineHeight: "45px",
                    }}
                  >
                    DNI/CUIT/CUIL
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#5b5b5f",
                      color: "white",
                      width: "20%",
                      padding: "0 16px",
                      height: "45px !important",
                      lineHeight: "45px",
                    }}
                  >
                    Contacto
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#5b5b5f",
                      color: "white",
                      width: "11%",
                      padding: "0 16px",
                      height: "45px !important",
                      lineHeight: "45px",
                    }}
                  >
                    Ubicación
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#5b5b5f",
                      color: "white",
                      width: "10%",
                      padding: "0 16px",
                      height: "45px !important",
                      lineHeight: "45px",
                    }}
                  >
                    Acciones
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {usuarios.length > 0 ? (
                  usuarios.map((user) => (
                    <TableRow
                      key={user.id}
                      hover
                      sx={{
                        height: "35px",
                        "&:nth-of-type(even)": {
                          backgroundColor: "#F7F7F7",
                        },
                        "&:hover": {
                          backgroundColor: "#E3EAFD",
                          transition: "background 0.3s ease",
                        },
                        transition: "background-color 0.2s",
                      }}
                    >
                      <TableCell
                        sx={{
                          padding: "0 16px",
                          borderBottom: "1px solid #ccc",
                          height: "35px",
                        }}
                      >
                        <Chip
                          label={user.id}
                          size="small"
                          variant="outlined"
                          sx={{
                            fontWeight: 500,
                            minWidth: 40,
                            justifyContent: "center",
                          }}
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "0 16px",
                          borderBottom: "1px solid #ccc",
                          height: "35px",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <PersonIcon fontSize="small" sx={{ color: "#5b5b5f", mr: 1 }} />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {user.usuario || "—"}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "0 16px",
                          borderBottom: "1px solid #ccc",
                          height: "35px",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Typography variant="body2">
                            {showPasswordForId === user.id ? user.password : "••••••••"}
                          </Typography>
                          <IconButton
                            onClick={() => handleTogglePasswordVisibility(user.id)}
                            size="small"
                            sx={{
                              backgroundColor: "rgba(91, 91, 95, 0.1)",
                              color: "#5b5b5f",
                              "&:hover": {
                                backgroundColor: "rgba(91, 91, 95, 0.2)",
                              },
                            }}
                          >
                            {showPasswordForId === user.id ? (
                              <VisibilityOff fontSize="small" />
                            ) : (
                              <Visibility fontSize="small" />
                            )}
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "0 16px",
                          borderBottom: "1px solid #ccc",
                          height: "35px",
                        }}
                      >
                        <Typography variant="body2">
                          {`${user.nombre || ""} ${user.apellido || ""}`.trim() || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "0 16px",
                          borderBottom: "1px solid #ccc",
                          height: "35px",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <BadgeIcon fontSize="small" sx={{ color: "#5b5b5f", mr: 1 }} />
                          <Typography variant="body2">{user.dniCuitCuil || "—"}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "0 16px",
                          borderBottom: "1px solid #ccc",
                          height: "35px",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                          }}
                        >
                          {user.email && (
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <EmailIcon fontSize="small" sx={{ color: "#5b5b5f", mr: 1 }} />
                              <Typography variant="body2" noWrap>
                                {user.email}
                              </Typography>
                            </Box>
                          )}
                          {user.telefono && (
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <PhoneIcon fontSize="small" sx={{ color: "#5b5b5f", mr: 1 }} />
                              <Typography variant="body2">{user.telefono}</Typography>
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "0 16px",
                          borderBottom: "1px solid #ccc",
                          height: "35px",
                        }}
                      >
                        <Tooltip
                          title={`${user.direccion || ""} ${user.localidad || ""} ${user.provincia || ""}`}
                          arrow
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 120,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {user.localidad ? `${user.localidad}, ${user.provincia || ""}` : "—"}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          padding: "0 16px",
                          borderBottom: "1px solid #ccc",
                          height: "35px",
                        }}
                      >
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <Tooltip title="Editar usuario">
                            <IconButton
                              onClick={() => editarUsuario(user)}
                              size="small"
                              sx={{
                                backgroundColor: "rgba(91, 91, 95, 0.1)",
                                color: "#5b5b5f",
                                "&:hover": {
                                  backgroundColor: "rgba(91, 91, 95, 0.2)",
                                },
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {user.rol !== 1 && (
                            <Tooltip title="Eliminar usuario">
                              <IconButton
                                onClick={() => setConfirmDelete({ open: true, usuario: user })}
                                size="small"
                                sx={{
                                  backgroundColor: "rgba(211, 47, 47, 0.1)",
                                  color: "#d32f2f",
                                  "&:hover": {
                                    backgroundColor: "rgba(211, 47, 47, 0.2)",
                                  },
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {user.rol === 1 && (
                            <Tooltip title="No se puede eliminar un administrador">
                              <span>
                                <IconButton
                                  disabled
                                  size="small"
                                  sx={{
                                    backgroundColor: "rgba(211, 47, 47, 0.05)",
                                    color: "#999",
                                    opacity: 0.5,
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                      {loading ? (
                        <Typography color="text.secondary">Cargando usuarios...</Typography>
                      ) : searchText ? (
                        <Typography color="text.secondary">
                          No se encontraron usuarios con ese criterio de búsqueda
                        </Typography>
                      ) : (
                        <Typography color="text.secondary">No hay usuarios registrados</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <CustomPagination
            currentPage={page}
            totalPages={totalPages}
            totalCount={totalCount}
            onPageChange={handlePageChange}
            vieneDe="tablaUsuarios"
          />
        </Box>
      </Card>

      <ModalEditarUsuario
        open={openModalEditarUsuario}
        handleClose={handleCerrarModalCrearUsuario}
        setOpen={setOpenModalEditarUsuario}
        usuario={usuarioEditar}
      />

      <Dialog open={confirmDelete.open} onClose={() => setConfirmDelete({ open: false, usuario: null })}>
        <DialogTitle>Eliminar usuario</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            Vas a eliminar al usuario <strong>{confirmDelete.usuario?.usuario}</strong>. Esta acción es un borrado lógico (eliminado = true).
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete({ open: false, usuario: null })}>Cancelar</Button>
          <Button 
            color="error" 
            variant="contained" 
            onClick={async () => {
              try {
                await deleteUsuarioSoft(confirmDelete.usuario.id)
                await fetchUsuarios()
                setSuccess(true)
                setTimeout(() => setSuccess(false), 3000)
              } catch (e) {
                setError("No se pudo eliminar el usuario")
              } finally {
                setConfirmDelete({ open: false, usuario: null })
              }
            }}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
